import { Group, Box, Button, Container, Loader, Radio, Stack, Text, Title } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getEmpresasFinalistasByCategoria } from "@/modules/empresas/firestore";
import { Parameters_ } from "@/src/routes/votar-empresa-inspiradora-externo-page/components/VotarEmpresaInspiradoraExternoPage";

const TEMPO_DELAY = 800;

type Pagina3Properties = {
  categoria: string;
  onSubmeterVoto: (voto: { empresa: string; categoria: string }) => void;
  onVoltar: () => void;
};

const Pagina3 = ({ categoria, onSubmeterVoto, onVoltar }: Pagina3Properties) => {
  const { licenciadaId, empresaId } = useParams<Parameters_>();
  const [empresaSelecionada, setEmpresaSelecionada] = useState<string | null>(null);
  const [empresas, setEmpresas] = useState<string[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const carregarEmpresas = async () => {
      setCarregando(true);
      // eslint-disable-next-line compat/compat
      await new Promise((resolve) => setTimeout(resolve, TEMPO_DELAY));

      // Using the optimized function that searches status first, then empresas
      const resultado = await getEmpresasFinalistasByCategoria(categoria, licenciadaId || "", empresaId || "");

      setEmpresas(resultado);
      setCarregando(false);
    };

    void carregarEmpresas();
  }, [categoria, empresaId, licenciadaId]);

  const handleSubmit = useCallback(() => {
    if (empresaSelecionada) {
      onSubmeterVoto({ empresa: empresaSelecionada, categoria });
    }
  }, [empresaSelecionada, onSubmeterVoto, categoria]);

  const createEmpresaClickHandler = useCallback((empresa: string) => {
    return () => {
      setEmpresaSelecionada(empresa);
    };
  }, []);

  const createKeyDownHandler = useCallback((empresa: string) => {
    return (event: React.KeyboardEvent) => {
      if (event.key === " " || event.key === "Enter") {
        setEmpresaSelecionada(empresa);
      }
    };
  }, []);
  if (carregando) {
    return (
      <Container size="sm" py="xl">
        <Stack align="center">
          <Text>Carregando empresas para {categoria}...</Text>
          <Loader />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Stack align="center">
        <Title order={3}>Qual empresa mais inspira inovação na categoria {categoria}?</Title>

        <Radio.Group
          value={empresaSelecionada || ""}
          onChange={setEmpresaSelecionada}
          name="empresas"
          variant="vertical"
          w="100%"
        >
          {empresas.map((empresa) => (
            <Box
              key={empresa}
              component="button"
              type="button"
              onClick={createEmpresaClickHandler(empresa)}
              tabIndex={0}
              style={{
                backgroundColor: empresaSelecionada === empresa ? "#f1f7ff" : "#f9f9f9",
                border: empresaSelecionada === empresa ? "2px solid #228be6" : "1px solid #ccc",
                borderRadius: "12px",
                padding: "12px 16px",
                transition: "all 0.2s ease",
                cursor: "pointer",
                width: "100%",
                display: "flex",
                alignItems: "center",
                marginBottom: 12,
                outline: "none"
              }}
              onKeyDown={createKeyDownHandler(empresa)}
            >
              <Radio
                value={empresa}
                checked={empresaSelecionada === empresa}
                readOnly
                tabIndex={-1}
                size="md"
                radius="md"
                label={empresa}
                styles={{
                  root: { margin: 0, background: "none", border: "none", boxShadow: "none", padding: 0, flex: 1 },
                  radio: { marginRight: "12px" },
                  label: { fontSize: "16px", fontWeight: 500, color: "#333" }
                }}
              />
            </Box>
          ))}
        </Radio.Group>

        <Box mt="lg" w="100%">
          <Group justify="space-between">
            <Button variant="outline" onClick={onVoltar}>
              Voltar
            </Button>
            <Button onClick={handleSubmit} disabled={!empresaSelecionada}>
              Submeter Voto
            </Button>
          </Group>
        </Box>
      </Stack>
    </Container>
  );
};

export default Pagina3;
